import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';

/**
 * SEO Component
 *
 * Manages meta tags, title, and canonical URLs for each page.
 * Uses react-helmet-async to dynamically update head tags.
 *
 * @param {string} title - Page title (will be suffixed with " - Feeling Froggy")
 * @param {string} description - Meta description for the page
 * @param {string} canonical - Canonical URL (absolute URL including domain)
 * @param {string|string[]} keywords - Keywords (string or array)
 * @param {string} ogImage - Open Graph image URL (optional)
 * @param {string} ogType - Open Graph type (default: "website")
 * @param {Array} additionalMeta - Additional meta tags
 * @param {object|object[]} jsonLd - JSON-LD structured data
 */
// Non-prod hosts must never be indexed (beta staging). Belt-and-
// suspenders alongside the backend robots.txt Disallow: any SEO page
// on a beta.* host emits noindex regardless of the `noindex` prop.
const isNonProdHost = () => {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname || '';
  return h.includes('beta.') || h === 'localhost' || h.startsWith('127.') || h.endsWith('.local');
};

// The site's own title — the one a page with no title of its own reads.
// NotFoundPage puts it back when the 404 leaves (Cowork 2026-10-23: the
// 404 title stuck on every later route that sets none).
// ⚠️ ONE WORDING (Cowork 2026-10-23: after the 404 the tab read "Feeling
// Froggy - …" while a fresh load read "Feeling Froggy LLC – …"). This is
// public/index.html's <title> — the title every page that sets none of its
// own shows on first paint, and the og/twitter/manifest name — word for
// word, en dash included. The static HTML cannot import it, so
// `SiteTitleOneWording.test.js` pins index.html and manifest.json to it.
export const DEFAULT_SITE_TITLE = 'Feeling Froggy LLC – Large Obligate Dart Frogs & Select Geckos';

const SEO = ({
  title = DEFAULT_SITE_TITLE,
  description = 'Specialty breeder of large obligate dart frogs (Oophaga) and select geckos — leachianus, Black Night & Mandarin leopard geckos. Captive-bred, live-arrival guaranteed, with expert care guides.',
  canonical = 'https://feelingfroggy.llc/',
  keywords = 'large obligate dart frogs, oophaga, poison dart frogs, leachianus gecko, leopard geckos, live arrival guarantee',
  ogImage = 'https://feelingfroggy.llc/logos/ff_original_96px.png',
  ogType = 'website',
  additionalMeta = [],
  jsonLd = null,
  noindex = false,
}) => {
  // Format title - add site name if not already present
  const fullTitle = title.includes('Feeling Froggy') ? title : `${title} - Feeling Froggy`;

  // Normalize keywords to array
  const keywordArray = Array.isArray(keywords)
    ? keywords
    : (typeof keywords === 'string' ? keywords.split(',').map(k => k.trim()).filter(Boolean) : []);

  const keywordString = keywordArray.join(', ');

  // Normalize JSON-LD to array
  const jsonLdArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  // Robots directive: force noindex on non-prod hosts or when the page
  // explicitly opts out (thin/duplicate pages). Otherwise index,follow.
  const robotsContent = (noindex || isNonProdHost()) ? 'noindex, nofollow' : 'index, follow';

  return (
    <Helmet key={canonical}>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywordArray.length > 0 && <meta name="keywords" content={keywordString} />}
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={canonical} />

      {/* Additional Meta Tags */}
      {additionalMeta.map((meta, i) => (
        <meta key={`am-${i}`} {...meta} />
      ))}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Feeling Froggy" />

      {/* Twitter — large image card for richer previews */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD Structured Data */}
      {jsonLdArray.map((data, i) => (
        <script key={`jsonld-${i}`} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  canonical: PropTypes.string,
  keywords: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  ogImage: PropTypes.string,
  ogType: PropTypes.string,
  additionalMeta: PropTypes.arrayOf(PropTypes.object),
  jsonLd: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object),
  ]),
  noindex: PropTypes.bool,
};

export default SEO;
